import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Search, Users } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NAVY = "#1e2130";
const SURFACE = "#292d44";
const CORAL = "#f76359";

const PHOTOS = [
  "/brand/hero-patio.jpg",
  "/brand/venue-park.jpg",
  "/brand/venue-cafe-inclusive.jpg",
  "/brand/group-patio-recovery.jpg",
  "/brand/group-park-recovery.jpg",
];
const ALT = "A diverse, welcoming Bible study group in a public place — people of many races, ages, body sizes and abilities, including wheelchair users, a pregnant woman, elders and children, together with open Bibles";
const OT = new Set(["Genesis", "Exodus", "Leviticus", "Numbers", "Deuteronomy", "Joshua", "Judges", "Ruth",
  "1 Samuel", "2 Samuel", "1 Kings", "2 Kings", "1 Chronicles", "2 Chronicles", "Ezra", "Nehemiah", "Esther",
  "Job", "Psalms", "Psalm", "Proverbs", "Ecclesiastes", "Song of Solomon", "Isaiah", "Jeremiah", "Lamentations",
  "Ezekiel", "Daniel", "Hosea", "Joel", "Amos", "Obadiah", "Jonah", "Micah", "Nahum", "Habakkuk", "Zephaniah",
  "Haggai", "Zechariah", "Malachi"]);
const CHIPS = ["All", "Coffee shops", "Parks", "Tonight", "Hebrew", "Greek"];

function coralPin(selected) {
  const size = selected ? 42 : 32;
  const html = `<svg width="${size}" height="${size * 1.24}" viewBox="0 0 34 42" xmlns="http://www.w3.org/2000/svg">
    <path d="M17 0C8.2 0 1 7.1 1 15.9C1 27 17 42 17 42s16-15 16-26.1C33 7.1 25.8 0 17 0z" fill="#f76359" stroke="${selected ? "#fff" : "none"}" stroke-width="2"/>
    <circle cx="17" cy="16" r="6.2" fill="#fff"/></svg>`;
  return L.divIcon({ html, className: "", iconSize: [size, size * 1.24], iconAnchor: [size / 2, size * 1.24], popupAnchor: [0, -size] });
}

function Recenter({ center }) {
  const map = useMap();
  useEffect(() => { if (center) map.setView(center, 11, { animate: true }); }, [center, map]);
  return null;
}

function FitAll({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length > 1) map.fitBounds(points, { padding: [40, 40] });
    else if (points.length === 1) map.setView(points[0], 11);
  }, [points, map]);
  return null;
}

export default function Landing() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [studies, setStudies] = useState([]);
  const [chip, setChip] = useState("All");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => { api.get("/studies").then(({ data }) => setStudies(data.studies)); }, []);

  const withPhoto = useMemo(() => studies.map((s, i) => ({ ...s, photo: s.photo || PHOTOS[i % PHOTOS.length] })), [studies]);
  const today = new Date().getDay() === 0 ? 6 : new Date().getDay() - 1; // Mon=0

  const filtered = useMemo(() => withPhoto.filter((s) => {
    const cat = (s.venue.category || "").toLowerCase();
    if (chip === "Coffee shops" && !/cafe|coffee/.test(cat)) return false;
    if (chip === "Parks" && !/park/.test(cat)) return false;
    if (chip === "Tonight" && s.schedule.day_of_week !== today) return false;
    if (chip === "Hebrew" && !OT.has(s.book)) return false;
    if (chip === "Greek" && OT.has(s.book)) return false;
    if (q.trim()) {
      const hay = `${s.venue.name} ${s.venue.address} ${s.title}`.toLowerCase();
      if (!hay.includes(q.trim().toLowerCase())) return false;
    }
    return true;
  }), [withPhoto, chip, q, today]);

  const center = useMemo(() => {
    if (selected) { const s = filtered.find((x) => x.id === selected); if (s) return [s.venue.lat, s.venue.lng]; }
    if (filtered.length) {
      const la = filtered.reduce((a, s) => a + s.venue.lat, 0) / filtered.length;
      const ln = filtered.reduce((a, s) => a + s.venue.lng, 0) / filtered.length;
      return [la, ln];
    }
    return [39.5, -98.35];
  }, [filtered, selected]);

  const timeLabel = (s) => `${s.schedule.day_name}s · ${s.schedule.start_time}`;

  return (
    <div style={{ background: NAVY }} className="flex min-h-screen flex-col text-[#faf6f0]">
      {/* Top bar */}
      <header className="flex items-center gap-3 border-b border-[#faf6f0]/10 px-4 py-3 sm:px-6">
        <button onClick={() => nav("/")} aria-label="BiblePlaza home" data-testid="home-logo" className="shrink-0 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f76359]">
          <img src="/brand/bibleplaza-wordmark.png" alt="BiblePlaza" className="h-9 w-auto" />
        </button>
        <div className="relative mx-auto w-full max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#faf6f0]/50" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="home-search" placeholder="Search city or zip code"
            className="w-full rounded-full border border-[#faf6f0]/15 bg-[#292d44] py-2.5 pl-9 pr-4 text-sm text-[#faf6f0] placeholder-[#faf6f0]/45 focus:border-[#f76359] focus:outline-none" />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button onClick={() => nav(user ? "/plaza" : "/auth")} data-testid="home-signin" className="hidden rounded-full px-4 py-2 text-sm font-semibold text-[#faf6f0]/80 hover:text-white sm:block">{user ? "Open app" : "Sign in"}</button>
          <button onClick={() => nav(user?.tier === "curator" ? "/curator" : "/upgrade")} data-testid="home-host" className="rounded-full bg-[#f76359] px-4 py-2 text-sm font-semibold text-white hover:bg-[#e2554c]">Host a study</button>
        </div>
      </header>

      {/* Split view */}
      <div className="flex flex-1 flex-col-reverse lg:flex-row">
        {/* LIST */}
        <aside className="flex w-full flex-col lg:w-[520px] lg:shrink-0">
          <div className="px-5 pt-6 sm:px-7">
            <h1 className="font-extrabold leading-[0.95] tracking-[-0.02em]" style={{ fontSize: "clamp(2rem,4vw,40px)" }}>
              Oh, We Pray <span style={{ color: CORAL }}>In Public.</span>
            </h1>
            <p className="mt-2 text-sm text-[#faf6f0]/70">Open groups near you, in public places.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {CHIPS.map((c) => (
                <button key={c} onClick={() => setChip(c)} data-testid={`chip-${c.toLowerCase().replace(/\s/g, "-")}`}
                  className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${chip === c ? "bg-[#f76359] text-white" : "bg-[#292d44] text-[#faf6f0]/75 hover:text-white"}`}>{c}</button>
              ))}
            </div>
          </div>
          <div className="mt-4 space-y-3 overflow-y-auto px-5 pb-6 sm:px-7 lg:max-h-[calc(100vh-320px)]">
            {filtered.length === 0 && <p className="py-8 text-center text-sm text-[#faf6f0]/50">No groups match. Try another filter.</p>}
            {filtered.map((s) => (
              <button key={s.id} data-testid={`home-card-${s.id}`}
                onMouseEnter={() => setSelected(s.id)} onClick={() => setSelected(s.id)}
                className={`flex w-full gap-3 rounded-[18px] p-3 text-left transition-transform hover:-translate-y-0.5 ${selected === s.id ? "ring-2 ring-[#f76359]" : ""}`}
                style={{ background: SURFACE }}>
                <img src={s.photo} alt={ALT} onError={(e) => { e.currentTarget.src = PHOTOS[0]; }} className="h-[110px] w-[150px] shrink-0 rounded-xl object-cover" />
                <div className="min-w-0 flex-1">
                  <h3 className="truncate font-bold text-[#faf6f0]">{s.title}</h3>
                  <p className="mt-0.5 text-xs text-[#faf6f0]/60">{timeLabel(s)}</p>
                  <p className="truncate text-xs text-[#faf6f0]/60">{s.venue.name} · {s.venue.category}</p>
                  <div className="mt-1.5 flex items-center justify-between">
                    <span className="flex items-center gap-1 text-xs text-[#faf6f0]/70"><Users size={13} className="text-[#f76359]" /> {s.member_count} going{s.capacity ? ` · cap ${s.capacity}` : ""}</span>
                    <span onClick={(e) => { e.stopPropagation(); nav(`/study/${s.id}`); }} className="rounded-full bg-[#f76359] px-3 py-1 text-xs font-bold text-white hover:bg-[#e2554c]">Join free</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </aside>

        {/* MAP */}
        <div className="h-[46vh] w-full lg:h-auto lg:flex-1">
          <MapContainer center={center} zoom={filtered.length ? 11 : 4} style={{ height: "100%", width: "100%", minHeight: "360px" }} scrollWheelZoom data-testid="home-map">
            <TileLayer attribution="&copy; OpenStreetMap contributors" url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {selected ? <Recenter center={center} /> : <FitAll points={filtered.map((s) => [s.venue.lat, s.venue.lng])} />}
            {filtered.map((s) => (
              <Marker key={s.id} position={[s.venue.lat, s.venue.lng]} icon={coralPin(selected === s.id)}
                eventHandlers={{ click: () => setSelected(s.id) }}>
                <Popup>
                  <div style={{ width: 210 }}>
                    <img src={s.photo} alt={ALT} style={{ width: "100%", height: 110, objectFit: "cover", borderRadius: 10 }} />
                    <div style={{ fontWeight: 800, marginTop: 6, color: "#1e2130" }}>{s.title}</div>
                    <div style={{ fontSize: 12, color: "#555", margin: "2px 0 8px" }}>Public venue · 3 minimum · {s.member_count} going</div>
                    <button onClick={() => nav(`/study/${s.id}`)} data-testid={`popup-join-${s.id}`}
                      style={{ background: CORAL, color: "#fff", border: "none", borderRadius: 999, padding: "8px 14px", fontWeight: 700, fontSize: 13, cursor: "pointer", width: "100%" }}>
                      Join this group
                    </button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </div>

      {/* Bottom coral strip */}
      <div style={{ background: CORAL }} className="px-4 py-3 text-center text-sm font-semibold text-[#1e2130]">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-center gap-x-3 gap-y-1">
          <span>Free to join</span><span aria-hidden>·</span>
          <span>Public venues only</span><span aria-hidden>·</span>
          <span>3+ people per group</span><span aria-hidden>·</span>
          <span>$4.99 to curate your own</span>
        </div>
      </div>
    </div>
  );
}
